import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useAuth } from '@/context/AuthContext';
import { useLanguage } from '@/context/LanguageContext';
import { Home, Search, Calendar, MessageCircle, Upload, User, LogOut, Menu, X, FileText, BarChart3, Bell } from 'lucide-react';
import { useState, useEffect } from 'react';
import { Avatar, AvatarFallback, AvatarImage } from '@/components/ui/avatar';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/dropdown-menu';
import AdBanner from '@/components/AdBanner';
import NotificationBell from '@/components/NotificationBell';
import LanguageToggle from '@/components/LanguageToggle';
import usePushNotifications from '@/hooks/usePushNotifications';
import { toast } from 'sonner';

const Layout = ({ children }) => {
  const { user, logout, token } = useAuth();
  const { t, language } = useLanguage();
  const location = useLocation();
  const navigate = useNavigate();
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const { requestPermission, notificationPermission, isSupported } = usePushNotifications(token);
  const [showNotificationPrompt, setShowNotificationPrompt] = useState(false);

  const isArtist = user?.user_type === 'independent_artist' || user?.user_type === 'studio_artist' || user?.user_type === 'studio' || user?.user_type === 'artist';

  // Show notification permission prompt once
  useEffect(() => {
    if (isSupported && notificationPermission === 'default' && user) {
      const hasAsked = localStorage.getItem('inker_notification_asked');
      if (!hasAsked) {
        setShowNotificationPrompt(true);
      }
    }
  }, [isSupported, notificationPermission, user]);

  const handleEnableNotifications = async () => {
    localStorage.setItem('inker_notification_asked', 'true');
    setShowNotificationPrompt(false);
    const result = await requestPermission();
    if (result) {
      toast.success(language === 'es' ? '¡Notificaciones activadas!' : 'Notifications enabled!');
    }
  };

  const handleDismissNotificationPrompt = () => {
    localStorage.setItem('inker_notification_asked', 'true');
    setShowNotificationPrompt(false);
  };

  const navItems = [
    { path: '/feed', icon: Home, label: t('nav.feed') },
    { path: '/search', icon: Search, label: t('nav.search') },
    { path: '/appointments', icon: Calendar, label: t('nav.appointments') },
    { path: '/messages', icon: MessageCircle, label: t('nav.messages') },
    { path: '/quotes', icon: FileText, label: t('nav.quotes') },
    ...(isArtist ? [
      { path: '/upload', icon: Upload, label: t('nav.upload') },
      { path: '/analytics', icon: BarChart3, label: 'Analytics' }
    ] : []),
  ];

  const handleLogout = () => {
    logout();
    navigate('/');
  };

  return (
    <div className="min-h-screen bg-zinc-950">
      {/* Desktop Navigation */}
      <nav className="hidden md:flex fixed top-0 left-0 right-0 z-50 glass h-16 items-center justify-between px-8">
        <Link to="/feed" className="flex items-center gap-2" data-testid="logo-link">
          <span className="font-['Cinzel'] text-xl font-bold tracking-wider text-white">INKER</span>
        </Link>

        <div className="flex items-center gap-8">
          {navItems.map((item) => (
            <Link
              key={item.path}
              to={item.path}
              data-testid={`nav-${item.label.toLowerCase()}`}
              className={`flex items-center gap-2 text-sm uppercase tracking-wider transition-colors ${location.pathname === item.path ? 'text-white' : 'text-zinc-500 hover:text-white'
                }`}
            >
              <item.icon size={18} />
              <span className="hidden lg:inline">{item.label}</span>
            </Link>
          ))}
        </div>

        <div className="flex items-center gap-4">
          <LanguageToggle />
          <NotificationBell />

          <DropdownMenu>
            <DropdownMenuTrigger asChild>
              <button className="flex items-center gap-3 hover:opacity-80 transition-opacity" data-testid="user-menu-trigger">
                <Avatar className="h-8 w-8 border border-zinc-800">
                  <AvatarImage src={user?.avatar} alt={user?.name} />
                  <AvatarFallback className="bg-zinc-800 text-zinc-400 text-xs">
                    {user?.name?.charAt(0)?.toUpperCase()}
                  </AvatarFallback>
                </Avatar>
                <span className="text-sm text-zinc-400 hidden lg:inline">{user?.name}</span>
              </button>
            </DropdownMenuTrigger>
            <DropdownMenuContent align="end" className="w-48 bg-zinc-900 border-zinc-800">
              <DropdownMenuItem asChild>
                <Link to={`/profile/${user?.id}`} className="flex items-center gap-2 cursor-pointer" data-testid="profile-link">
                  <User size={16} />
                  {t('nav.profile')}
                </Link>
              </DropdownMenuItem>
              <DropdownMenuSeparator className="bg-zinc-800" />
              <DropdownMenuItem onClick={handleLogout} className="text-rose-400 cursor-pointer" data-testid="logout-btn">
                <LogOut size={16} className="mr-2" />
                {t('nav.logout')}
              </DropdownMenuItem>
            </DropdownMenuContent>
          </DropdownMenu>
        </div>
      </nav>

      {/* Mobile Navigation */}
      <nav className="md:hidden fixed top-0 left-0 right-0 z-50 glass h-14 flex items-center justify-between px-4 safe-top">
        <Link to="/feed" className="font-['Cinzel'] text-lg font-bold tracking-wider text-white">
          INKER
        </Link>
        <div className="flex items-center gap-2">
          <LanguageToggle />
          <NotificationBell />
          <button
            onClick={() => setMobileMenuOpen(!mobileMenuOpen)}
            className="text-white p-2"
            data-testid="mobile-menu-toggle"
          >
            {mobileMenuOpen ? <X size={24} /> : <Menu size={24} />}
          </button>
        </div>
      </nav>

      {/* Mobile Menu Overlay */}
      {mobileMenuOpen && (
        <div className="md:hidden fixed inset-0 z-40 bg-zinc-950/95 pt-14">
          <div className="flex flex-col p-6 gap-4">
            {navItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                onClick={() => setMobileMenuOpen(false)}
                className={`flex items-center gap-4 py-3 text-lg ${location.pathname === item.path ? 'text-white' : 'text-zinc-400'
                  }`}
              >
                <item.icon size={22} />
                {item.label}
              </Link>
            ))}
            <div className="h-px bg-zinc-800 my-2" />
            <Link
              to={`/profile/${user?.id}`}
              onClick={() => setMobileMenuOpen(false)}
              className="flex items-center gap-4 py-3 text-lg text-zinc-400"
            >
              <User size={22} />
              Mi Perfil
            </Link>
            <button
              onClick={handleLogout}
              className="flex items-center gap-4 py-3 text-lg text-rose-400"
            >
              <LogOut size={22} />
              Cerrar Sesión
            </button>
          </div>
        </div>
      )}

      {/* Bottom Navigation (Mobile) */}
      <nav className="md:hidden fixed bottom-0 left-0 right-0 z-50 bg-zinc-950/95 backdrop-blur-md border-t border-zinc-900 safe-bottom">
        <div className="flex items-center justify-around h-16">
          {navItems.slice(0, 5).map((item) => (
            <Link
              key={item.path}
              to={item.path}
              className={`flex flex-col items-center justify-center flex-1 h-full gap-1 transition-colors ${location.pathname === item.path ? 'text-white' : 'text-zinc-600'
                }`}
            >
              <item.icon size={20} />
              <span className="text-[10px] tracking-wider">
                {item.label.length > 8 ? item.label.slice(0, 7) + '…' : item.label}
              </span>
            </Link>
          ))}
        </div>
      </nav>

      {/* Push Notification Permission Prompt */}
      {showNotificationPrompt && (
        <div className="fixed bottom-20 md:bottom-4 left-4 right-4 md:left-auto md:right-4 md:w-96 z-50 bg-zinc-900 border border-zinc-800 rounded-lg p-4 shadow-xl animate-fade-in">
          <div className="flex items-start gap-3">
            <div className="w-10 h-10 rounded-full bg-zinc-800 flex items-center justify-center flex-shrink-0">
              <Bell className="w-5 h-5 text-zinc-400" />
            </div>
            <div className="flex-1">
              <h4 className="text-white font-medium text-sm">
                {language === 'es' ? '¿Activar notificaciones?' : 'Enable notifications?'}
              </h4>
              <p className="text-zinc-500 text-xs mt-1">
                {language === 'es'
                  ? 'Recibe alertas de nuevos mensajes, citas y cotizaciones'
                  : 'Get alerts for new messages, appointments and quotes'}
              </p>
              <div className="flex gap-2 mt-3">
                <button
                  onClick={handleEnableNotifications}
                  className="px-3 py-1.5 bg-white text-zinc-950 text-xs font-medium rounded hover:bg-zinc-200 transition-colors"
                >
                  {language === 'es' ? 'Activar' : 'Enable'}
                </button>
                <button
                  onClick={handleDismissNotificationPrompt}
                  className="px-3 py-1.5 text-zinc-400 text-xs hover:text-white transition-colors"
                >
                  {language === 'es' ? 'Ahora no' : 'Not now'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Main Content */}
      <main className="pt-14 md:pt-20 pb-24 md:pb-8">
        <div className="xl:flex xl:gap-6 xl:max-w-[90rem] xl:mx-auto xl:px-4">
          <div className="flex-1 min-w-0">
            {children}
          </div>
          {/* Desktop Sidebar Ad */}
          <aside className="hidden xl:block w-[300px] flex-shrink-0 pt-4">
            <div className="sticky top-24">
              <AdBanner slot="3497156484" format="rectangle" />
              <div className="mt-6">
                <AdBanner slot="3497156484" format="rectangle" />
              </div>
            </div>
          </aside>
        </div>
      </main>
    </div>
  );
};

export default Layout;